import Grid from '@mui/material/Grid'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { OBJECT_PROPERTIES_QUERY_KEY, useObjectProperties } from 'api/objectProperties/objectProperties.api'
import { SETTINGS_QUERY_KEY } from 'api/settings/settings.api'
import { useUnits } from 'api/units/units.api'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import { getDateTimePickerRangesFromPermissions } from 'helpers/settingsModal.helper/settingsModal.helper'
import { Moment } from 'moment'
import React, { ReactElement, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Step } from 'react-joyride'
import { Button, Card } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import SettingsModal from 'ui/components/Modals/SettingsModal'
import ModuleHeader from 'ui/components/ModuleHeader/ModuleHeader'
import { useChannel, useEvent } from 'ui/components/PusherContext/PusherContext'
import SettingsTable, { SettingsPermissions } from 'ui/components/SettingsTable/SettingsTable'
import SystemMessageBanner from 'ui/components/SystemMessageBanner/SystemMessageBanner'
import Tour from 'ui/components/Tour/Tour'
import View from 'ui/components/View/View'
import Datetime from 'utils/datetime/datetime'
import { SettingsCalendarItem, unitsToCalendarItems } from './Components/SettingsCalendarHelper'
import SettingsCalendarView, { CalendarView } from './Components/SettingsCalendarView'

const widgetConstants = {
  OPTIMIZE_SETTINGS_CALENDAR: `optimize-settings-calendar`,
  OPTIMIZE_SETTINGS_TABLE: `optimize-settings-table`,
}

const OptimizeSettingHeaderContentClass = `optSettingsHeader`

const calendarInfo: Array<Step> = [
  {
    target: widgetConstants.OPTIMIZE_SETTINGS_CALENDAR,
    content: `What deviations are planned? Which deviations has there been historically? The deviation settings calendar gives you the answer! \n\nSorted at unit level, you can easily create deviation settings and also get a good overview of your production planning. Click and hold the left mouse button over any unit and period to create a new deviation setting. \n\nNote that deviation settings affect the production plan for the entire system!`,
    title: `Deviation settings calendar`,
  },
]
const tableInfo: Array<Step> = [
  {
    target: widgetConstants.OPTIMIZE_SETTINGS_TABLE,
    content: `Here you can find all active properties for which you can create deviation settings for. \n\nIf 'Status' shows an orange icon '!', this means that some active deviation for a specific property is currently affecting the production plan for some of the current production plan hours. \n\nClick on 'Show/edit' to create, edit and delete current deviation settings.`,
    title: `Table showing all deviation settings`,
  },
]

const steps: Array<Step> = [...calendarInfo, ...tableInfo]

export default function OptimizeSettingsView(): ReactElement {
  const { t } = useTranslation()

  const startTime = Datetime.getStartOfDay(Datetime.getISONow(0))
  const endTime = Datetime.getEndOfDay(Datetime.getISONow(168))

  const [runTour, setRunTour] = useState<null | number>(null)
  const { systemId } = useAuth()

  const permissions: SettingsPermissions = {
    canEdit: useHasPermission(`change_plan_settings`),
    canEditHistoric: useHasPermission(`change_historic_plan_settings`),
  }
  const startEndTimeRange = getDateTimePickerRangesFromPermissions(permissions.canEditHistoric)

  const queryClient = useQueryClient()

  const { channel } = useChannel(systemId ? `aurora-django-setting-${systemId}` : undefined)

  useEvent(channel, `settings-changed`, () => {
    queryClient.invalidateQueries({
      queryKey: [SETTINGS_QUERY_KEY],
    })
    queryClient.invalidateQueries({
      queryKey: [OBJECT_PROPERTIES_QUERY_KEY],
    })
  })
  // NOTE: Moved up from  SettingsCalendarView.tsx
  const [currentCalendarView, setCurrentCalendarView] = useState<CalendarView>('productionPlan')
  const [currentPeriod, setCurrentPeriod] = useState<{ startTime: ISODateTime; endTime: ISODateTime }>({
    startTime: startTime,
    endTime: endTime,
  })

  // Same rule as the digital twin calendar: week and month are read off the period being left, picking the
  // later end of it. From the production plan (today + 7 days) that is the upcoming week or month once the
  // plan spills over a week or month boundary, otherwise the current one.
  function handleCalendarViewChange(view: CalendarView): void {
    setCurrentCalendarView(view)
    if (view === 'productionPlan') {
      setCurrentPeriod({
        startTime: Datetime.getStartOfDay(Datetime.getISONow(0)),
        endTime: Datetime.getEndOfDay(Datetime.getISONow(168)),
      })
      return
    }
    const timeToStartFrom = Datetime.getStartTimeForLatestMonth(currentPeriod.startTime, currentPeriod.endTime)
    setCurrentPeriod(Datetime.getStartAndEndOfTimePeriod(timeToStartFrom, view))
  }

  function updateCurrentPeriod(date: Moment): void {
    if (date) {
      setCurrentPeriod(Datetime.getStartTimeEndTimeOfMonthByDate(Datetime.toISOString(date)))
    }
  }

  function updateCurrentWeek(weekStartTime: ISODateTime): void {
    setCurrentPeriod(Datetime.getStartTimeEndTimeOfWeekByDate(weekStartTime))
  }

  // Keeps the previous period's settings up while the next one loads, so the calendar can fade them out
  // behind a spinner instead of going blank.
  const {
    data: objectProperties,
    isPending: isObjectPropertiesPending,
    isPlaceholderData: isObjectPropertiesPlaceholder,
  } = useObjectProperties({
    classification: `deviation`,
    start_time: currentPeriod.startTime,
    end_time: currentPeriod.endTime,
    queryConfig: { placeholderData: keepPreviousData },
  })
  const { data: units, isPending: isUnitsPending } = useUnits()

  const items = useMemo(() => {
    let combinedUnitAndSettings: SettingsCalendarItem[] = []
    if (objectProperties && units) {
      const filteredUnits = units?.filter((unit) => unit.visible_in_calendar)
      filteredUnits.sort((a, b) => a.display_name.toLowerCase().localeCompare(b.display_name.toLowerCase()))
      combinedUnitAndSettings = unitsToCalendarItems(objectProperties, filteredUnits)
    }
    return combinedUnitAndSettings
  }, [objectProperties, units])

  const closeModal = (): void => {
    setShowSettingsModal(false)
  }

  const [showSettingsModal, setShowSettingsModal] = useState(false)

  const classification = 'deviation'

  return (
    <View
      header={
        <div className={OptimizeSettingHeaderContentClass}>
          <ModuleHeader
            infoHeader={t(`Deviation settings`)}
            onInfoButtonClick={() => {
              setRunTour(0)
            }}
          >
            <Grid container direction="row" justifyContent="flex-end">
              <Button
                icon="fal fa-plus"
                primary
                onClick={() => {
                  setShowSettingsModal(true)
                }}
              >
                {t(`Create new setting`)}
              </Button>
            </Grid>
          </ModuleHeader>
        </div>
      }
    >
      <SystemMessageBanner module="optimizesettings" />
      <Tour
        isSingle={!!runTour}
        run={runTour !== null}
        setRunState={(state: boolean) => setRunTour(state ? 0 : null)}
        steps={runTour === 1 ? calendarInfo : runTour === 2 ? tableInfo : steps}
      />

      <SettingsModal
        closeModal={closeModal}
        open={showSettingsModal}
        newDeviationForm={true}
        permissions={permissions}
        startEndTimeRange={startEndTimeRange}
        classification={classification}
      />

      <Grid size={{ xs: 12 }}>
        <Card tourId={widgetConstants.OPTIMIZE_SETTINGS_CALENDAR} title={t(`Deviation settings - calendar`)}>
          <SettingsCalendarView
            items={items}
            currentCalendarView={currentCalendarView}
            currentPeriod={currentPeriod}
            isPeriodPending={isObjectPropertiesPending || isObjectPropertiesPlaceholder || isUnitsPending}
            setCurrentCalendarView={handleCalendarViewChange}
            permissions={permissions}
            startEndTimeRange={startEndTimeRange}
            updateCurrentPeriod={updateCurrentPeriod}
            updateCurrentWeek={updateCurrentWeek}
          />
        </Card>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Card tourId={widgetConstants.OPTIMIZE_SETTINGS_TABLE} title={t(`Deviation settings - table`)}>
          <SettingsTable
            permissions={permissions}
            startEndTimeRange={startEndTimeRange}
            classification={classification}
            tableOptions={{
              initialState: { hiddenColumns: ['category'] },
            }}
          />
        </Card>
      </Grid>
    </View>
  )
}
